// The point deepest inside a polygon (its "pole of inaccessibility", as Mapbox's polylabel): the spot farthest
// from every edge, holes included. Unlike the centroid (Leaflet's getCenter) it is always inside the area: never
// in a hole (a town inside its countryside area, whose centroid often lands in the town) and never outside a
// C-shaped area. Used for the funnel badge on areas the filters leave out (filteredAreaBadges.js).

// Squared distance from (px, py) to the segment a-b.
const segmentDistanceSq = (px, py, a, b) => {
    let x = a[0];
    let y = a[1];
    let dx = b[0] - x;
    let dy = b[1] - y;
    if (dx !== 0 || dy !== 0) {
        const t = (((px - x) * dx) + ((py - y) * dy)) / ((dx * dx) + (dy * dy));
        if (t > 1) {
            x = b[0];
            y = b[1];
        } else if (t > 0) {
            x += dx * t;
            y += dy * t;
        }
    }
    dx = px - x;
    dy = py - y;
    return (dx * dx) + (dy * dy);
};

// Distance from (x, y) to the polygon's nearest edge: positive inside, negative outside (or in a hole).
const signedDistance = (x, y, rings) => {
    let inside = false;
    let minSq = Infinity;
    rings.forEach((ring) => {
        for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
            const a = ring[i];
            const b = ring[j];
            if ((a[1] > y) !== (b[1] > y) && x < (((b[0] - a[0]) * (y - a[1])) / (b[1] - a[1])) + a[0]) inside = !inside;
            minSq = Math.min(minSq, segmentDistanceSq(x, y, a, b));
        }
    });
    return (inside ? 1 : -1) * Math.sqrt(minSq);
};

// A max-heap of cells by how far inside a point in them could possibly be.
const heapPush = (heap, cell) => {
    heap.push(cell);
    let i = heap.length - 1;
    while (i > 0) {
        const parent = (i - 1) >> 1;
        if (heap[parent].max >= cell.max) break;
        heap[i] = heap[parent];
        i = parent;
    }
    heap[i] = cell;
};
const heapPop = (heap) => {
    const top = heap[0];
    const last = heap.pop();
    if (heap.length) {
        let i = 0;
        for (;;) {
            const left = (2 * i) + 1;
            if (left >= heap.length) break;
            const right = left + 1;
            const child = right < heap.length && heap[right].max > heap[left].max ? right : left;
            if (heap[child].max <= last.max) break;
            heap[i] = heap[child];
            i = child;
        }
        heap[i] = last;
    }
    return top;
};

// The ring's centroid (the usual first guess), or its first point when it has no area.
const ringCentroid = (ring) => {
    let area = 0;
    let cx = 0;
    let cy = 0;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
        const f = (ring[j][0] * ring[i][1]) - (ring[i][0] * ring[j][1]);
        area += f;
        cx += (ring[j][0] + ring[i][0]) * f;
        cy += (ring[j][1] + ring[i][1]) * f;
    }
    return area ? [cx / (3 * area), cy / (3 * area)] : ring[0];
};

// rings: [outer, ...holes], each a list of [x, y] (open or closed). Returns [x, y].
export const polygonLabelPoint = (rings, { precisionShare = 0.01, maxCells = 4000 } = {}) => {
    const outer = rings[0] || [];
    if (outer.length < 3) return outer[0] || [0, 0];
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    outer.forEach(([x, y]) => {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
    });
    const width = maxX - minX;
    const height = maxY - minY;
    const cellSize = Math.min(width, height);
    if (!cellSize) return [minX, minY];
    // Close enough: a share of the area's size (the badge needs a good spot, not the exact best one).
    const precision = Math.max(width, height) * precisionShare;
    const cell = (x, y, half) => {
        const d = signedDistance(x, y, rings);
        return { x, y, half, d, max: d + (half * Math.SQRT2) };
    };
    const heap = [];
    const half = cellSize / 2;
    for (let x = minX; x < maxX; x += cellSize) {
        for (let y = minY; y < maxY; y += cellSize) heapPush(heap, cell(x + half, y + half, half));
    }
    const [cx, cy] = ringCentroid(outer);
    let best = cell(cx, cy, 0);
    const middle = cell(minX + (width / 2), minY + (height / 2), 0);
    if (middle.d > best.d) best = middle;
    let probed = 0;
    while (heap.length && probed < maxCells) {
        const next = heapPop(heap);
        probed += 1;
        if (next.d > best.d) best = next;
        if (next.max - best.d <= precision) continue;
        const quarter = next.half / 2;
        heapPush(heap, cell(next.x - quarter, next.y - quarter, quarter));
        heapPush(heap, cell(next.x + quarter, next.y - quarter, quarter));
        heapPush(heap, cell(next.x - quarter, next.y + quarter, quarter));
        heapPush(heap, cell(next.x + quarter, next.y + quarter, quarter));
    }
    return [best.x, best.y];
};
